import type { BatchStatusResponse } from '@reach/shared-types/partner-batches';
import { TENANT_HEADER } from '@reach/shared-types/partner-openapi';
import {
  partnerApi,
  type ApiError,
  type ApiResponse,
  type BillableEventsResponse,
  type PartnerUserDetailsResponse,
  type PartnerUsersResponse,
} from './api';

interface UserResourceReference {
  schemaId: string;
  schemaName: string;
  count: number;
  referenceField: string;
  resourceIds: string[];
  category: string;
}

export interface UserResourceRelationships {
  primaryUserSchemaName: string;
  inboundReferences: UserResourceReference[];
  outboundReferences: UserResourceReference[];
}

export type TenantResourceData = Record<string, unknown>;

export interface TenantResourcesPage {
  results: TenantResourceData[];
  pagination: {
    total?: number;
    hasNextPage: boolean;
    cursor?: string | null;
  };
}

interface PartnerUsersOptions {
  limit?: number;
  cursor?: string;
  search?: string;
  status?: string;
}

interface BillableEventsOptions {
  limit?: number;
  cursor?: string;
  searchTerm?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  sortField?: string;
  sortDirection?: string;
}

interface ResourcesOptions {
  schemaDefinitionId: string;
  cursor?: string;
  limit?: number;
}

interface ResourceLookup {
  schemaDefinitionId: string;
  externalId: string;
}

type QueryValue = string | number | undefined;

const TENANT_QUERY_KEY_ROOT = 'tenant';

const withQuery = (path: string, query: Record<string, QueryValue>): string => {
  const params = new URLSearchParams();
  for (const [name, value] of Object.entries(query)) {
    const hasValue = value !== undefined && value !== '';
    if (hasValue) params.append(name, String(value));
  }
  const queryString = params.toString();
  return queryString ? `${path}?${queryString}` : path;
};

const resourcesPath = (schemaDefinitionId: string): string =>
  `/api/resources/${encodeURIComponent(schemaDefinitionId)}`;

const isNotFoundError = (error: unknown): boolean => (error as ApiError).response?.status === 404;

export class TenantApiClient {
  constructor(readonly tenantId: string) {}

  queryKey<const Parts extends readonly unknown[]>(...parts: Parts) {
    return [TENANT_QUERY_KEY_ROOT, this.tenantId, ...parts] as const;
  }

  request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    return partnerApi.request<T>(endpoint, {
      ...options,
      headers: { ...options.headers, [TENANT_HEADER]: this.tenantId },
    });
  }

  getPartnerUsers(options: PartnerUsersOptions = {}): Promise<PartnerUsersResponse> {
    return this.request<PartnerUsersResponse>(withQuery('/api/partner-users', { ...options }));
  }

  getPartnerUserDetails(externalId: string): Promise<PartnerUserDetailsResponse> {
    return this.request<PartnerUserDetailsResponse>(
      `/api/partner-users/${encodeURIComponent(externalId)}/details`,
    );
  }

  getUserResourceRelationships(externalId: string): Promise<ApiResponse<UserResourceRelationships>> {
    return this.request<ApiResponse<UserResourceRelationships>>(
      `/api/partner-users/${encodeURIComponent(externalId)}/relationships`,
    );
  }

  getBillableEvents(options: BillableEventsOptions = {}): Promise<BillableEventsResponse> {
    return this.request<BillableEventsResponse>(
      withQuery('/api/orders/billable-events', { ...options }),
    );
  }

  async listResources({
    schemaDefinitionId,
    cursor,
    limit,
  }: ResourcesOptions): Promise<TenantResourcesPage> {
    const response = await this.request<ApiResponse<TenantResourcesPage>>(
      withQuery(resourcesPath(schemaDefinitionId), { cursor, limit }),
    );
    return response.data;
  }

  // null rather than undefined: TanStack Query rejects undefined query data.
  async findResourceByExternalId({
    schemaDefinitionId,
    externalId,
  }: ResourceLookup): Promise<TenantResourceData | null> {
    try {
      const response = await this.request<ApiResponse<{ result: TenantResourceData }>>(
        `${resourcesPath(schemaDefinitionId)}/${encodeURIComponent(externalId)}`,
      );
      return response.data?.result ?? null;
    } catch (error) {
      if (isNotFoundError(error)) {
        return null;
      }
      throw error;
    }
  }

  async getBatchStatus(batchId: string): Promise<BatchStatusResponse> {
    const response = await this.request<ApiResponse<BatchStatusResponse>>(
      `/api/batches/${encodeURIComponent(batchId)}`,
    );
    return response.data;
  }
}
